import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Coins, X, Check, Gift } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from './ui/dialog';
import { Button } from './ui/button';
import { useUserData } from './UserDataContext';
import { toast } from 'sonner';
import { isInviteCompleted } from '../services/inviteService';
import { EVENTS, SCREENS } from '../constants/navigation';
import goldIcon from '../assets/gold icon.png';
import { SafeStorage } from '../utils/safeStorage';

export const DailyRewardManager = ({ currentScreen, blockShow }: { currentScreen?: string, blockShow?: boolean }) => {
    const { userProfile, claimDailyStreak, firebaseUser, authLoading } = useUserData();
    const [isOpen, setIsOpen] = useState(false);
    const [isEligible, setIsEligible] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
    const [currentStreak, setCurrentStreak] = useState(1);
    const [isClaiming, setIsClaiming] = useState(false);
    const [inviteCompleted, setInviteCompleted] = useState(false);

    const rewards = [25, 50, 100, 200, 300, 400, 500];

    useEffect(() => {
        if (!userProfile?.id) return;
        isInviteCompleted(String(userProfile.id))
            .then(done => setInviteCompleted(done))
            .catch(() => {});
    }, [userProfile?.id, isOpen]);

    useEffect(() => {
        if (!firebaseUser || authLoading) return;

        const checkEligibility = () => {
            const now = new Date();
            let lastClaimAt: Date | null = null;

            if (userProfile.goldLastClaimAt) {
                // Handle both Firestore Timestamp and JS Date
                if (typeof userProfile.goldLastClaimAt.toDate === 'function') {
                    lastClaimAt = userProfile.goldLastClaimAt.toDate();
                } else {
                    lastClaimAt = new Date(userProfile.goldLastClaimAt);
                }
            }

            let streak = userProfile.goldStreakDay || 1;

            if (lastClaimAt) {
                const hoursSinceLastClaim = (now.getTime() - lastClaimAt.getTime()) / (1000 * 60 * 60);

                if (hoursSinceLastClaim < 24) {
                    setIsEligible(false);
                    // Calculate remaining time
                    const targetTime = new Date(lastClaimAt.getTime() + 24 * 60 * 60 * 1000);
                    const diffMs = targetTime.getTime() - now.getTime();
                    
                    if (diffMs > 0) {
                        const h = Math.floor(diffMs / (1000 * 60 * 60));
                        const m = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        const s = Math.floor((diffMs % (1000 * 60)) / 1000);
                        setTimeRemaining(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
                    } else {
                        setTimeRemaining(null);
                    }
                    setCurrentStreak(streak); // They are locked, so show current streak visually
                } else if (hoursSinceLastClaim >= 48) {
                    setIsEligible(true);
                    setTimeRemaining(null);
                    setCurrentStreak(1); // Missed a day — reset to day 1
                } else {
                    setIsEligible(true);
                    setTimeRemaining(null);
                    // Next claim will be streak + 1 (or wrap back to 1 after day 7)
                    const nextStreak = streak >= 7 ? 1 : streak + 1;
                    setCurrentStreak(nextStreak);
                }
            } else {
                setIsEligible(true);
                setTimeRemaining(null);
                setCurrentStreak(1);
            }
        };

        const isProfileComplete = !!(userProfile?.username && userProfile?.dateOfBirth && userProfile?.country);
        const isSetupScreen = [SCREENS.PROFILE_SETUP, SCREENS.ONBOARDING, SCREENS.LOGIN, SCREENS.SIGNUP].includes(currentScreen as any);

        checkEligibility();
        const interval = setInterval(checkEligibility, 1000);

        // Auto-show logic: show whenever they open/foreground the app and are eligible
        // 🔒 SECURITY GUARD: NEVER show during onboarding, if profile is incomplete, or if blocked by higher priority modal
        let autoShowTimeout: NodeJS.Timeout | null = null;
        
        // Use SafeStorage to ensure we only alert them ONCE per eligible period
        let lastClaimTimeStr = 'never';
        if (userProfile?.goldLastClaimAt) {
            if (typeof userProfile.goldLastClaimAt.toDate === 'function') {
                lastClaimTimeStr = userProfile.goldLastClaimAt.toDate().getTime().toString();
            } else {
                lastClaimTimeStr = new Date(userProfile.goldLastClaimAt).getTime().toString();
            }
        }
        const alertKey = `daily_reward_alert_${userProfile?.id}_${lastClaimTimeStr}`;

        if (isEligible && isProfileComplete && !isSetupScreen && !blockShow) {
            const hasAlerted = SafeStorage.getItem(alertKey);
            if (!hasAlerted) {
                autoShowTimeout = setTimeout(() => {
                    setIsOpen(true);
                    SafeStorage.setItem(alertKey, 'true');
                }, 1000);
            }
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isEligible && isProfileComplete && !isSetupScreen && !blockShow) {
                const hasAlerted = SafeStorage.getItem(alertKey);
                if (!hasAlerted) {
                    setIsOpen(true);
                    SafeStorage.setItem(alertKey, 'true');
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            if (autoShowTimeout) clearTimeout(autoShowTimeout);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [firebaseUser, authLoading, userProfile.goldLastClaimAt, userProfile.goldStreakDay, isEligible, blockShow, currentScreen]);

    const handleClaim = async () => {
        if (!isEligible || isClaiming) return;
        setIsClaiming(true);
        try {
            await claimDailyStreak();
            // Optional: Close modal after a short delay so they see the success state
            setTimeout(() => {
                setIsOpen(false);
            }, 1500);
        } catch (error) {
            console.error("Error claiming daily streak", error);
        } finally {
            setIsClaiming(false);
        }
    };

    if (!firebaseUser) return null;

    // Derived UI state
    // Base amount for the current pending day
    const amountToClaim = rewards[Math.min(currentStreak - 1, 6)];
    
    // 🛡️ SECURITY & FEEDBACK: Multipliers only apply if there is an ACTIVE subscription tier
    const isActive = !!userProfile.subscriptionActive;
    let multiplier = 1;
    if (isActive) {
        if (userProfile.subscriptionTier === 'yapper') multiplier = 2;
        if (userProfile.subscriptionTier === 'elite') multiplier = 3;
    }
    const finalAmountToClaim = amountToClaim * multiplier;

    return (
        <>
            {/* Floating Trigger */}
            <AnimatePresence>
                {(!isOpen && currentScreen === SCREENS.EXPLORE) && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 20 }}
                        onClick={() => setIsOpen(true)}
                        className={`fixed flex flex-col items-center justify-center gap-0.5 z-50 w-14 h-14 rounded-full shadow-xl transition-transform hover:scale-105 active:scale-95`}
                        style={{ 
                            bottom: '100px', 
                            right: '20px',
                            background: (isEligible || !inviteCompleted) 
                                ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' 
                                : '#1a1a1a' // Dark mode gray if all tasks done
                        }}
                    >
                        {(isEligible || !inviteCompleted) && (
                            <span className="absolute inset-0 rounded-full animate-ping opacity-30 bg-[#3B82F6]"></span>
                        )}
                        <Gift className={`w-6 h-6 relative z-10 ${(isEligible || !inviteCompleted) ? 'text-white' : 'text-white/50'}`} />
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Modal Overlay explicitly styled like Invite Modal */}
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent 
                    className="max-w-[95vw] sm:max-w-md border-black/5 dark:border-white/10 text-black dark:text-white p-0 shadow-2xl overflow-hidden rounded-[32px] z-9999"
                    style={{ background: 'linear-gradient(160deg, #fffbef 0%, #fff8e1 100%)' }}
                >
                    <div className="hidden dark:block absolute inset-0" style={{ background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 100%)' }} />
                    
                    <div className="relative z-10 flex flex-col p-6 gap-6">

                        <DialogHeader className="p-0 border-b-0 space-y-2 text-center pb-2">
                        <div className="flex justify-center mb-2">
                            <motion.div 
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="w-16 h-16 shrink-0 rounded-full bg-linear-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center shadow-[0_8px_25px_rgba(59,130,246,0.5)] border-2 border-white/20 group relative overflow-hidden"
                            >
                                <img src={goldIcon as any} alt="Gold" className="h-8 w-auto object-contain drop-shadow-lg" style={{ maxHeight: '32px' }} />
                            </motion.div>
                        </div>
                        <DialogTitle className="text-3xl font-black tracking-tight mb-1 font-[Outfit]">Rewards Hub</DialogTitle>
                        <p className="text-black/60 dark:text-white/60 text-sm font-medium">Complete tasks to earn Gold</p>
                    </DialogHeader>

                    {/* Primary Panel: Daily Reward */}
                    <div className="bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-2xl p-4">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="font-bold text-sm text-black dark:text-white flex items-center gap-1.5"><img src={goldIcon as any} alt="Gold" className="h-4 w-auto object-contain drop-shadow-sm" /> Daily Login</h3>
                        </div>

                        {/* Streak Tracker Grid */}
                        <div className="w-full flex justify-between items-center relative py-2 mb-4">
                        {/* Connecting Line */}
                        <div className="absolute left-6 right-6 h-[2px] bg-black/10 dark:bg-white/10 top-1/2 -translate-y-1/2 z-0" />
                        
                        {[1, 2, 3, 4, 5, 6, 7].map((day) => {
                            let state: 'claimed' | 'today' | 'upcoming' = 'upcoming';
                            
                            if (isEligible) {
                                if (day < currentStreak) state = 'claimed';
                                else if (day === currentStreak) state = 'today';
                            } else {
                                if (day <= currentStreak) state = 'claimed';
                            }

                            return (
                                <div key={day} className="relative z-10 flex flex-col items-center gap-1">
                                    <div 
                                        className={`w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-black transition-all shadow-md
                                            ${state === 'claimed' 
                                                ? 'bg-linear-to-r from-[#3B82F6] to-[#2563EB] dark:from-[#4478ea] dark:to-[#3498DB] text-white border-none shadow-[#3B82F6]/30 dark:shadow-[#4478ea]/30' 
                                                : state === 'today'
                                                ? 'bg-white border-2 border-[#3B82F6] ring-4 ring-[#3B82F6]/30 scale-110 animate-pulse'
                                                : 'bg-white dark:bg-[#1a1a1a] text-black/40 dark:text-white/40 border-2 border-black/10 dark:border-white/10'
                                            }
                                        `}
                                    >
                                        {state === 'claimed' ? (
                                            <Check className="w-4 h-4" />
                                        ) : (
                                            <span style={state === 'today' ? { color: '#3B82F6' } : {}}>{day}</span>
                                        )}
                                    </div>
                                    <span className={`text-xs font-medium ${state === 'upcoming' ? 'text-black/40 dark:text-white/40' : 'text-[#3B82F6] dark:text-[#3B82F6]'}`}>
                                        +{rewards[day - 1] * (state === 'claimed' ? 1 : multiplier)}
                                    </span>
                                </div>
                            );
                        })}
                        </div>
                        
                        <Button 
                            onClick={handleClaim}
                            disabled={!isEligible || isClaiming}
                            className="w-full h-12 rounded-xl font-bold text-base bg-linear-to-r from-[#3B82F6] to-[#2563EB] dark:from-[#4478ea] dark:to-[#3498DB] text-white shadow-lg border-none hover:opacity-90 active:scale-[0.98] transition-all"
                        >
                            {isClaiming ? 'Claiming...' : isEligible ? `Claim ${finalAmountToClaim} Gold` : `Wait ${timeRemaining || '...'}`}
                        </Button>
                        {(multiplier > 1 && isEligible) && (
                            <p className="mt-4 text-center text-xs text-[#3B82F6] dark:text-[#4478ea] font-medium">Includes {multiplier}x Subscription Bonus</p>
                        )}
                    </div>

                    {/* Secondary Panel: Invite Friends */}
                    <div className="bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-2xl p-4 flex items-center justify-between">
                        <div>
                            <h3 className="font-bold text-sm text-black dark:text-white flex items-center gap-1.5 mb-0.5">
                                <Gift className="w-4 h-4 text-blue-500" />
                                Invite Friends
                            </h3>
                            <p className="text-xs text-black/60 dark:text-white/60 font-medium">Invite 5 friends, earn 500 Gold!</p>
                        </div>
                        {inviteCompleted ? (
                            <div className="flex items-center gap-1 text-xs font-bold text-green-600 dark:text-green-400 bg-green-500/10 px-3 py-1.5 rounded-full">
                                <Check className="w-3.5 h-3.5" /> Completed
                            </div>
                        ) : (
                            <Button 
                                onClick={() => {
                                    setIsOpen(false);
                                    window.dispatchEvent(new CustomEvent(EVENTS.SHOW_INVITE_MODAL as string));
                                }}
                                className="h-8 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md border-none px-4"
                            >
                                Start Task
                            </Button>
                        )}
                    </div>
                  </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
